import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { getMergeConfig, hasMergeConfig } from "@/config/mergeConfigRegistry";
import { generateSelectFieldsFromConfig, validateFetchedDataForMerge } from "@/utils/mergeConfigUtils";

// Import the specific fetch functions
import { fetchCandidatesByIds } from "@/hooks/candidates/fetch-candidates";
import { fetchJobsByIds } from "@/hooks/jobs/fetch-jobs";
import { fetchMatchesByIds } from "@/hooks/matches/fetch-matches";
import { fetchCompaniesByIds } from "@/hooks/companies/fetch-companies";

// Generic entity fetcher interface
interface EntityFetcher<T = any> {
	fetchByIds: (ids: string[]) => Promise<T[]>;
	entityName: string;
	tableName: string;
	selectFields: string;
	mergeConfig?: any; // The merge configuration for this entity
}

/**
 * Generate select fields for an entity based on its merge configuration
 * Falls back to basic fields if no merge config is available
 */
function getSelectFieldsForEntity(entityName: string, tableName: string): string {
	if (hasMergeConfig(entityName)) {
		const config = getMergeConfig(entityName);
		if (config) {
			const selectFields = generateSelectFieldsFromConfig(config);
			//console.log(`📋 Generated select fields for ${entityName} from merge config:`, selectFields);
			return selectFields;
		}
	}
	
	// Fallback to basic fields if no merge config available
	console.warn(`⚠️ No merge config found for ${entityName}, using basic fallback fields`);
	return "id, created_at, updated_at";
}

// Entity fetchers using the specific fetch functions and merge configurations
const candidateFetcher: EntityFetcher = {
	entityName: "candidates",
	tableName: "candidates",
	selectFields: getSelectFieldsForEntity("candidates", "candidates"),
	fetchByIds: fetchCandidatesByIds,
	mergeConfig: getMergeConfig("candidates")
};

const jobFetcher: EntityFetcher = {
	entityName: "jobs",
	tableName: "jobs", 
	selectFields: getSelectFieldsForEntity("jobs", "jobs"),
	fetchByIds: fetchJobsByIds,
	mergeConfig: getMergeConfig("jobs")
};

const matchFetcher: EntityFetcher = {
	entityName: "matches",
	tableName: "matches",
	selectFields: getSelectFieldsForEntity("matches", "matches"),
	fetchByIds: fetchMatchesByIds,
	mergeConfig: getMergeConfig("matches")
};

const companyFetcher: EntityFetcher = {
	entityName: "companies",
	tableName: "companies",
	selectFields: getSelectFieldsForEntity("companies", "companies"),
	fetchByIds: fetchCompaniesByIds,
	mergeConfig: getMergeConfig("companies")
};

// Entity fetcher registry
const entityFetchers = {
	candidates: candidateFetcher,
	jobs: jobFetcher,
	matches: matchFetcher,
	companies: companyFetcher,
} as const;

// Generic function to fetch fresh data for any entity type
export const fetchFreshEntityData = async <T = any>(
	entityLabel: string,
	ids: string[]
): Promise<T[]> => {
	try {
		// Get the appropriate fetcher for this entity type
		const fetcher = entityFetchers[entityLabel as keyof typeof entityFetchers];
		
		if (!fetcher) {
			console.warn(`No fetcher found for entity type: ${entityLabel}. Using fallback.`);
			return [];
		}

		//console.log(`🔄 Fetching fresh ${entityLabel} data from database...`, ids.length, "items");
		//console.log(`📋 Using select fields:`, fetcher.selectFields);
		
		const freshData = await fetcher.fetchByIds(ids);
		
		// Validate the fetched data against merge configuration if available
		if (fetcher.mergeConfig) {
			const validation = validateFetchedDataForMerge(freshData, fetcher.mergeConfig);
			if (!validation.isValid) {
				console.warn(`⚠️ Fetched ${entityLabel} data is missing required fields for merge:`, validation.missingFields);
				toast({
					title: `Warning: Missing required fields`,
					description: `Some required fields for ${entityLabel} merge are missing: ${validation.missingFields.join(', ')}`,
					variant: "destructive",
				});
			} else {
				//console.log(`✅ Fetched ${entityLabel} data validation passed`);
			}
		}
		
		//console.log(`✅ Fresh ${entityLabel} data loaded:`, freshData.length, "items");
		return freshData as T[];
		
	} catch (error) {
		console.error(`Failed to fetch fresh ${entityLabel} data:`, error);
		toast({
			title: `Failed to load fresh ${entityLabel} data`,
			description: "Using cached data instead.",
			variant: "destructive",
		});
		throw error;
	}
};

// Helper to check if an entity type supports fresh data fetching
export const supportsEntityFetching = (entityLabel: string): boolean => {
	return entityLabel in entityFetchers;
};
